import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import type { ApartmentDefaults, RentDetail } from '../types/apartments';
import { RentDetailLine } from './RentDetailLine';
import { datadogRum } from '@datadog/browser-rum';

function formatDate(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatDate_MM_YYYY(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}_${yyyy}`;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface PdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'receipt' | 'payment-notice' | 'charge-regularization' | 'rent-increase';
  slug: string;
  apartment: ApartmentDefaults;
}

export const PdfModal: React.FC<PdfModalProps> = ({ isOpen, onClose, type, slug, apartment }) => {
  const today = useMemo(() => new Date(), []);
  const { t } = useTranslation();
  
  const [moneyDate, setMoneyDate] = useState<string>(
    type === 'receipt' 
      ? formatDate(today)
      : formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 5))
  );
  const [forMonth, setForMonth] = useState<Date>(
    type === 'receipt'
      ? new Date(today.getFullYear(), today.getMonth(), 1)
      : new Date(today.getFullYear(), today.getMonth() + 1, 1)
  );
  const [generating, setGenerating] = useState<boolean>(false);
  const [isPartialMonth, setIsPartialMonth] = useState<boolean>(false);
  const [startDay, setStartDay] = useState<number>(1);
  const [endDay, setEndDay] = useState<number>(
    new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  );

  // Charge regularization specific state
  // Default period is the previous year
  const regularizationYear = today.getFullYear() - 1;
  const yearStart = formatDate(new Date(regularizationYear, 0, 1));
  
  // Compute default start date: lease start date if within the year, otherwise Jan 1st
  const getDefaultPeriodStart = () => {
    const leaseStartDate = apartment.apartment?.leaseStartDate;
    if (leaseStartDate) {
      // Parse lease start date (format: DD/MM/YYYY)
      const parts = leaseStartDate.split('/');
      if (parts.length === 3) {
        const leaseYear = parseInt(parts[2], 10);
        // If lease started in the regularization year, use lease start date
        if (leaseYear === regularizationYear) {
          return leaseStartDate;
        }
      }
    }
    // Default to January 1st of the regularization year
    return yearStart;
  };

  const [periodStart, setPeriodStart] = useState<string>(getDefaultPeriodStart());
  const [periodEnd, setPeriodEnd] = useState<string>(
    formatDate(new Date(regularizationYear, 11, 31))
  );
  const [totalProvisionPaid, setTotalProvisionPaid] = useState<number>(0);
  const [actualCharges, setActualCharges] = useState<RentDetail[]>([
    { title: 'Charges de copropriété récupérables', value: 0 },
    { title: "Taxe d'enlèvement des ordures ménagères", value: 0 }
  ]);
  const [settlementMethod, setSettlementMethod] = useState<'next_rent' | 'bank_wire'>('next_rent');
  const [nextRentDueDate, setNextRentDueDate] = useState<string>(
    formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 5))
  );
  
  // Initialize rent details from apartment data
  const initialRentDetails = useMemo(() => {
    if (apartment.apartment?.rentDetails && apartment.apartment.rentDetails.length > 0) {
      return apartment.apartment.rentDetails.map(d => ({ ...d }));
    }
    // Fall back to rent and charges for backward compatibility
    return [
      { title: 'Loyer', value: apartment.apartment?.rent || 0 },
      { title: 'Provision pour charges', value: apartment.apartment?.charges || 0 }
    ];
  }, [apartment.apartment]);
  
  const [baseRentDetails, setBaseRentDetails] = useState<RentDetail[]>(initialRentDetails);
  const [rentDetails, setRentDetails] = useState<RentDetail[]>(initialRentDetails);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [chargeRegValidationErrors, setChargeRegValidationErrors] = useState<{ provision?: string; charges?: Record<number, string> }>({});
  const [manuallyEditedIndices, setManuallyEditedIndices] = useState<Set<number>>(new Set());

  // Next rent details as RentDetail array for reusing RentDetailLine component
  const [nextRentDetails, setNextRentDetails] = useState<RentDetail[]>([
    { title: 'Loyer', value: apartment.apartment?.rent || 0 },
    { title: 'Provision pour charges', value: apartment.apartment?.charges || 0 }
  ]);

  // Rent increase specific state
  const currentYear = today.getFullYear();
  const leaseStartDate = apartment.apartment?.leaseStartDate || '';
  // Revision date = same day/month as lease, current year
  const getDefaultRevisionDate = () => {
    const parts = leaseStartDate.split('/');
    if (parts.length === 3) {
      return `${parts[0]}/${parts[1]}/${currentYear}`;
    }
    return formatDate(today);
  };
  const getIRLQuarter = (date: string) => {
    const parts = date.split('/');
    if (parts.length !== 3) return '';
    const month = parseInt(parts[1], 10);
    if (month >= 1 && month <= 3) return '1er trimestre';
    if (month >= 4 && month <= 6) return '2ème trimestre';
    if (month >= 7 && month <= 9) return '3ème trimestre';
    return '4ème trimestre';
  };
  const [revisionDate, setRevisionDate] = useState<string>(getDefaultRevisionDate());
  const [documentDate] = [formatDate(today)];
  const [oldIRL, setOldIRL] = useState<number>(0);
  const [newIRL, setNewIRL] = useState<number>(0);
  const [rentHC, setRentHC] = useState<number>(apartment.apartment?.rent || 0);
  const [newRent, setNewRent] = useState<number>(0);
  const [newRentManuallyEdited, setNewRentManuallyEdited] = useState<boolean>(false);
  const [dpeClass, setDpeClass] = useState<string>(apartment.apartment?.dpeClass || '');
  const [revisionValidationErrors, setRevisionValidationErrors] = useState<Record<string, string>>({});

  const maxIndexedRent = oldIRL > 0 ? Math.round((rentHC * newIRL / oldIRL) * 100) / 100 : 0;

  useEffect(() => {
    if (!newRentManuallyEdited && maxIndexedRent > 0) {
      setNewRent(maxIndexedRent);
    }
  }, [maxIndexedRent, newRentManuallyEdited]);

  // Update day range when forMonth changes and reset manual edits
  useEffect(() => {
    const daysInMonth = new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate();
    setStartDay(1);
    setEndDay(daysInMonth);
    setManuallyEditedIndices(new Set()); // Reset manual edits when month changes
  }, [forMonth]);

  // Track previous partial month state to detect toggle
  const [prevIsPartialMonth, setPrevIsPartialMonth] = useState(isPartialMonth);
  
  useEffect(() => {
    // If partial month mode was just enabled, reset manual edits
    if (isPartialMonth && !prevIsPartialMonth) {
      setManuallyEditedIndices(new Set());
    }
    setPrevIsPartialMonth(isPartialMonth);
  }, [isPartialMonth, prevIsPartialMonth]);

  // Calculate prorated rent when partial month mode or dates change
  useEffect(() => {
    if (isPartialMonth) {
      // Calculate the number of days in the custom period
      const daysInPeriod = endDay - startDay + 1; // +1 to include both start and end days
      
      // Calculate total days in the month
      const daysInMonth = new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate();
      
      // Calculate proration factor
      const prorationFactor = daysInPeriod / daysInMonth;
      
      // Apply proration to base rent details, but skip manually edited ones
      setRentDetails(prevRentDetails => {
        return baseRentDetails.map((detail, index) => {
          // If this index was manually edited, keep the current value
          if (manuallyEditedIndices.has(index)) {
            return prevRentDetails[index] || detail;
          }
          
          const baseValue = typeof detail.value === 'string' ? parseFloat(detail.value) || 0 : detail.value;
          return {
            ...detail,
            value: Math.round(baseValue * prorationFactor * 100) / 100
          };
        });
      });
    } else {
      // Reset to base rent details when not in partial month mode
      setRentDetails(baseRentDetails.map(d => ({ ...d })));
      // Clear manual edits when exiting partial month mode
      setManuallyEditedIndices(new Set());
    }
  }, [isPartialMonth, startDay, endDay, forMonth, baseRentDetails, manuallyEditedIndices]);

  if (!isOpen) return null;
  
  const addRentDetail = () => {
    const newDetail = { title: '', value: 0 };
    setBaseRentDetails([...baseRentDetails, newDetail]);
    setRentDetails([...rentDetails, newDetail]);
  };
  
  const removeRentDetail = (index: number) => {
    // Don't allow removing first two lines (Loyer and Charges)
    if (index < 2) {
      return;
    }
    setBaseRentDetails(baseRentDetails.filter((_, i) => i !== index));
    setRentDetails(rentDetails.filter((_, i) => i !== index));
    
    // Update manually edited indices - remove this index and adjust higher indices
    setManuallyEditedIndices(prev => {
      const newSet = new Set<number>();
      prev.forEach(i => {
        if (i < index) {
          newSet.add(i);
        } else if (i > index) {
          newSet.add(i - 1); // Shift down by 1
        }
        // Skip the removed index
      });
      return newSet;
    });
    
    // Clear validation error for removed line
    const newErrors = { ...validationErrors };
    delete newErrors[index];
    setValidationErrors(newErrors);
  };
  
  const updateRentDetail = (index: number, field: 'title' | 'value', value: string | number) => {
    // Update the rentDetails directly
    const updated = [...rentDetails];
    if (field === 'title') {
      updated[index] = { ...updated[index], title: value as string };
      // Also update base rent details for title to prevent useEffect from resetting it
      const updatedBase = [...baseRentDetails];
      updatedBase[index] = { ...updatedBase[index], title: value as string };
      setBaseRentDetails(updatedBase);
    } else {
      updated[index] = { ...updated[index], value: value };
      
      // Mark this index as manually edited if in partial month mode
      if (isPartialMonth) {
        setManuallyEditedIndices(prev => new Set(prev).add(index));
      } else {
        // If not in partial month mode, also update base rent details
        const updatedBase = [...baseRentDetails];
        updatedBase[index] = { ...updatedBase[index], value: value };
        setBaseRentDetails(updatedBase);
      }
    }
    setRentDetails(updated);
    
    // Clear validation error when user starts typing
    if (field === 'title' && validationErrors[index]) {
      const newErrors = { ...validationErrors };
      delete newErrors[index];
      setValidationErrors(newErrors);
    }
  };
  
  const totalRent = rentDetails.reduce((sum, detail) => {
    const value = typeof detail.value === 'string' ? parseFloat(detail.value) || 0 : detail.value;
    return sum + value;
  }, 0);

  // Charge regularization computed values
  const totalActualCharges = actualCharges.reduce((sum, charge) => {
    const value = typeof charge.value === 'string' ? parseFloat(charge.value) || 0 : charge.value;
    return sum + value;
  }, 0);
  const balance = totalProvisionPaid - totalActualCharges;
  const balanceFavorsTenant = balance > 0;
  const absBalance = Math.abs(balance);
  
  // Compute next rent totals from nextRentDetails
  const nextRentDetailsTotal = nextRentDetails.reduce((sum, detail) => {
    const value = typeof detail.value === 'string' ? parseFloat(detail.value) || 0 : detail.value;
    return sum + value;
  }, 0);
  
  // Next rent total with regularization
  const regularizationAmount = balanceFavorsTenant ? -absBalance : absBalance;
  const nextRentTotal = nextRentDetailsTotal + regularizationAmount;
  
  const validateRentDetails = (): boolean => {
    const errors: Record<number, string> = {};
    
    // Validate additional lines (index >= 2) have titles
    rentDetails.slice(2).forEach((detail, i) => {
      const actualIndex = i + 2;
      if (!detail.title || detail.title.trim() === '') {
        errors[actualIndex] = t('pdfModal.errorTitleRequired');
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const baseSettings: Record<string, unknown> = {
    date: formatDate(today),
    tenants: apartment.tenants,
    apartment: {
      ...apartment.apartment,
      rentDetails: rentDetails.map(detail => ({
        ...detail,
        value: typeof detail.value === 'string' ? parseFloat(detail.value) || 0 : detail.value
      }))
    },
    owner: apartment.owner,
  };

  // Charge regularization handlers
  const addActualCharge = () => {
    setActualCharges([...actualCharges, { title: '', value: 0 }]);
  };

  const removeActualCharge = (index: number) => {
    setActualCharges(actualCharges.filter((_, i) => i !== index));
  };

  const updateActualCharge = (index: number, field: 'title' | 'value', value: string | number) => {
    const updated = [...actualCharges];
    if (field === 'title') {
      updated[index] = { ...updated[index], title: value as string };
    } else {
      updated[index] = { ...updated[index], value: value };
    }
    setActualCharges(updated);
    // Clear validation error when user updates
    if (chargeRegValidationErrors.charges?.[index]) {
      const newChargeErrors = { ...chargeRegValidationErrors.charges };
      delete newChargeErrors[index];
      setChargeRegValidationErrors({ ...chargeRegValidationErrors, charges: newChargeErrors });
    }
  };

  // Next rent detail handlers
  const addNextRentDetail = () => {
    setNextRentDetails([...nextRentDetails, { title: '', value: 0 }]);
  };

  const removeNextRentDetail = (index: number) => {
    if (index < 2) return; // Don't remove default lines (Loyer and Charges)
    setNextRentDetails(nextRentDetails.filter((_, i) => i !== index));
  };

  const updateNextRentDetail = (index: number, field: 'title' | 'value', value: string | number) => {
    const updated = [...nextRentDetails];
    if (field === 'title') {
      updated[index] = { ...updated[index], title: value as string };
    } else {
      updated[index] = { ...updated[index], value: value };
    }
    setNextRentDetails(updated);
  };

  // Charge regularization validation
  const validateChargeRegularization = (): boolean => {
    const errors: { provision?: string; charges?: Record<number, string> } = {};
    
    if (totalProvisionPaid <= 0) {
      errors.provision = t('pdfModal.errorProvisionRequired');
    }
    
    // Validate all charge lines have titles
    const chargeErrors: Record<number, string> = {};
    actualCharges.forEach((charge, index) => {
      if (!charge.title || charge.title.trim() === '') {
        chargeErrors[index] = t('pdfModal.errorTitleRequired');
      }
    });
    if (Object.keys(chargeErrors).length > 0) {
      errors.charges = chargeErrors;
    }
    
    setChargeRegValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function handleGenerate() {
    // Validate form
    if (type === 'charge-regularization') {
      if (!validateChargeRegularization()) {
        return;
      }
    } else if (type === 'rent-increase') {
      const errors: Record<string, string> = {};
      if (oldIRL <= 0) errors.oldIRL = t('pdfModal.errorOldIRLRequired');
      if (newIRL <= 0) errors.newIRL = t('pdfModal.errorNewIRLRequired');
      if (newRent <= 0) errors.newRent = t('pdfModal.errorNewRentRequired');
      setRevisionValidationErrors(errors);
      if (Object.keys(errors).length > 0) return;
    } else if (!validateRentDetails()) {
      return;
    }
    
    setGenerating(true);
    
    const actionStartTime = performance.now();
    
    if (type === 'rent-increase') {
      const rentIncreaseSettings = {
        date: documentDate,
        tenants: apartment.tenants,
        apartment: apartment.apartment,
        owner: apartment.owner,
        leaseStartDate: leaseStartDate,
        revisionDate,
        oldIRL,
        newIRL,
        rentHC,
        newRent,
        dpeClass,
      };

      const fileName = `${slug}-revision-loyer-${revisionDate.split('/')[2]}.pdf`;

      try {
        const blob = await apiService.generateRentIncrease(rentIncreaseSettings);
        downloadBlob(blob, fileName);
        datadogRum.addAction('generate_rent_increase_pdf', {
          apartment_slug: slug,
          apartment_name: apartment.name,
          revision_date: revisionDate,
          duration_ms: performance.now() - actionStartTime,
          status: 'success',
        });
        onClose();
      } catch (error) {
        datadogRum.addAction('generate_rent_increase_pdf', {
          apartment_slug: slug,
          apartment_name: apartment.name,
          revision_date: revisionDate,
          duration_ms: performance.now() - actionStartTime,
          status: 'error',
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
        alert(`${t('pdfModal.errorGenerating')} ${type}: ` + (error instanceof Error ? error.message : 'Unknown error'));
      } finally {
        setGenerating(false);
      }
      return;
    }

    if (type === 'charge-regularization') {
      // Get rent and charges from nextRentDetails
      const rentValue = typeof nextRentDetails[0]?.value === 'string' 
        ? parseFloat(nextRentDetails[0].value) || 0 
        : nextRentDetails[0]?.value || 0;
      const chargesValue = typeof nextRentDetails[1]?.value === 'string' 
        ? parseFloat(nextRentDetails[1].value) || 0 
        : nextRentDetails[1]?.value || 0;

      const chargeRegSettings = {
        date: formatDate(today),
        tenants: apartment.tenants,
        apartment: apartment.apartment,
        owner: apartment.owner,
        leaseStartDate: apartment.apartment?.leaseStartDate || periodStart,
        regularizationPeriod: {
          start: periodStart,
          end: periodEnd,
        },
        totalProvisionPaid: totalProvisionPaid,
        actualCharges: actualCharges.map(charge => ({
          ...charge,
          value: typeof charge.value === 'string' ? parseFloat(charge.value) || 0 : charge.value
        })),
        settlementMethod: settlementMethod,
        ...(settlementMethod === 'next_rent' && {
          nextRent: {
            dueDate: nextRentDueDate,
            rent: rentValue,
            charges: chargesValue,
          }
        }),
      };
      
      const fileName = `${slug}-regularisation-charges-${periodEnd.split('/')[2]}.pdf`;
      
      try {
        const blob = await apiService.generateChargeRegularization(chargeRegSettings);
        downloadBlob(blob, fileName);
        
        datadogRum.addAction('generate_charge_regularization_pdf', {
          apartment_slug: slug,
          apartment_name: apartment.name,
          period_end: periodEnd,
          duration_ms: performance.now() - actionStartTime,
          status: 'success',
        });
        
        onClose();
      } catch (error) {
        datadogRum.addAction('generate_charge_regularization_pdf', {
          apartment_slug: slug,
          apartment_name: apartment.name,
          period_end: periodEnd,
          duration_ms: performance.now() - actionStartTime,
          status: 'error',
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
        alert(`${t('pdfModal.errorGenerating')} ${type}: ` + (error instanceof Error ? error.message : 'Unknown error'));
      } finally {
        setGenerating(false);
      }
      return;
    }
    
    // Use custom dates if partial month mode is enabled, otherwise use full month
    const start = isPartialMonth 
      ? new Date(forMonth.getFullYear(), forMonth.getMonth(), startDay)
      : new Date(forMonth.getFullYear(), forMonth.getMonth(), 1);
    const end = isPartialMonth 
      ? new Date(forMonth.getFullYear(), forMonth.getMonth(), endDay)
      : new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0);

    const specific = {
      ...baseSettings,
      ...(type === 'receipt' 
        ? { moneyReceiptDate: moneyDate }
        : { moneyDueDate: moneyDate }
      ),
      timePeriod: {
        start: formatDate(start),
        end: formatDate(end),
      },
    };
    
    const actionName = type === 'receipt' ? 'generate_receipt_pdf' : 'generate_payment_notice_pdf';
    const fileName = type === 'receipt' 
      ? `${slug}-quittance-${formatDate_MM_YYYY(forMonth)}.pdf`
      : `${slug}-avis-echeance-${formatDate_MM_YYYY(forMonth)}.pdf`;
    
    try {
      const blob = type === 'receipt'
        ? await apiService.generateReceipt(specific)
        : await apiService.generatePaymentNotice(specific);
      
      downloadBlob(blob, fileName);
      
      datadogRum.addAction(actionName, {
        apartment_slug: slug,
        apartment_name: apartment.name,
        for_month: formatDate_MM_YYYY(forMonth),
        duration_ms: performance.now() - actionStartTime,
        status: 'success',
      });
      
      onClose();
    } catch (error) {
      datadogRum.addAction(actionName, {
        apartment_slug: slug,
        apartment_name: apartment.name,
        for_month: formatDate_MM_YYYY(forMonth),
        duration_ms: performance.now() - actionStartTime,
        status: 'error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      alert(`${t('pdfModal.errorGenerating')} ${type}: ` + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  }

  const title = type === 'receipt' 
    ? t('pdfModal.quittanceTitle') 
    : type === 'payment-notice'
      ? t('pdfModal.echeanceTitle')
      : type === 'charge-regularization'
        ? t('pdfModal.regularisationTitle')
        : t('pdfModal.revisionTitle');
  const description = type === 'receipt' 
    ? t('pdfModal.quittanceDescription')
    : type === 'payment-notice'
      ? t('pdfModal.echeanceDescription')
      : type === 'charge-regularization'
        ? t('pdfModal.regularisationDescription')
        : t('pdfModal.revisionDescription');
  const dateLabel = type === 'receipt' ? t('pdfModal.moneyReceiptDate') : t('pdfModal.moneyDueDate');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>
      <div className={`bg-white w-full rounded-lg shadow-xl ${type === 'charge-regularization' || type === 'rent-increase' ? 'max-w-xl' : 'max-w-md'}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center">
            {type === 'receipt' ? (
              <svg className="w-6 h-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ) : type === 'payment-notice' ? (
              <svg className="w-6 h-6 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ) : type === 'charge-regularization' ? (
              <svg className="w-6 h-6 text-teal-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-orange-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            )}
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-gray-600 mb-6">{description}</p>
          
          {type === 'charge-regularization' ? (
            /* Charge Regularization Form */
            <div className="space-y-4">
              {/* Regularization Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.regularizationPeriod')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('pdfModal.periodStart')}</label>
                    <input
                      type="date"
                      value={periodStart.split('/').reverse().join('-')}
                      onChange={(e) => {
                        const [yyyy, mm, dd] = e.target.value.split('-');
                        setPeriodStart(`${dd}/${mm}/${yyyy}`);
                      }}
                      className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('pdfModal.periodEnd')}</label>
                    <input
                      type="date"
                      value={periodEnd.split('/').reverse().join('-')}
                      onChange={(e) => {
                        const [yyyy, mm, dd] = e.target.value.split('-');
                        setPeriodEnd(`${dd}/${mm}/${yyyy}`);
                      }}
                      className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* Provision Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.totalProvisionPaid')}</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={totalProvisionPaid}
                    onChange={(e) => {
                      setTotalProvisionPaid(parseFloat(e.target.value) || 0);
                      if (chargeRegValidationErrors.provision) {
                        setChargeRegValidationErrors({ ...chargeRegValidationErrors, provision: undefined });
                      }
                    }}
                    className={`w-full rounded-md border p-2 pr-8 text-sm focus:outline-none focus:ring-2 ${
                      chargeRegValidationErrors.provision 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500'
                    }`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                </div>
                {chargeRegValidationErrors.provision && (
                  <p className="text-xs text-red-600 mt-1">{chargeRegValidationErrors.provision}</p>
                )}
              </div>

              {/* Actual Charges */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">{t('pdfModal.actualCharges')}</label>
                  <button
                    type="button"
                    onClick={addActualCharge}
                    className="text-sm px-3 py-1 rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('pdfModal.addChargeLine')}
                  </button>
                </div>
                <div className="space-y-2">
                  {actualCharges.map((charge, index) => (
                    <RentDetailLine
                      key={index}
                      detail={charge}
                      index={index}
                      isReadOnly={false}
                      isRemovable={true}
                      validationError={chargeRegValidationErrors.charges?.[index]}
                      onUpdateTitle={(value) => updateActualCharge(index, 'title', value)}
                      onUpdateValue={(value) => updateActualCharge(index, 'value', value)}
                      onRemove={() => removeActualCharge(index)}
                    />
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-teal-200 bg-teal-50 rounded-md p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">{t('pdfModal.totalActualCharges')}</span>
                    <span className="text-lg font-bold text-gray-900">{totalActualCharges.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              {/* Balance Display */}
              <div className={`p-4 rounded-md ${balanceFavorsTenant ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('pdfModal.balance')}</span>
                    <span className={`ml-2 text-xs px-2 py-1 rounded-full ${balanceFavorsTenant ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {balanceFavorsTenant ? t('pdfModal.balanceFavorsTenant') : t('pdfModal.balanceFavorsLandlord')}
                    </span>
                  </div>
                  <span className={`text-xl font-bold ${balanceFavorsTenant ? 'text-green-700' : 'text-orange-700'}`}>
                    {balanceFavorsTenant ? '+' : '-'}{absBalance.toFixed(2)}€
                  </span>
                </div>
              </div>

              {/* Settlement Method */}
              <div className="pt-4 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-3">{t('pdfModal.settlementMethod')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSettlementMethod('next_rent')}
                    className={`p-3 rounded-md border-2 text-sm font-medium transition-all ${
                      settlementMethod === 'next_rent'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {t('pdfModal.settlementNextRent')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettlementMethod('bank_wire')}
                    className={`p-3 rounded-md border-2 text-sm font-medium transition-all ${
                      settlementMethod === 'bank_wire'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {t('pdfModal.settlementBankWire')}
                  </button>
                </div>
              </div>

              {/* Next Rent Details (conditional) */}
              {settlementMethod === 'next_rent' && (
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">{t('pdfModal.nextRentDetails')}</label>
                    <button
                      type="button"
                      onClick={addNextRentDetail}
                      className="text-sm px-3 py-1 rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('pdfModal.addLine')}
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t('pdfModal.nextRentDueDate')}</label>
                      <input
                        type="date"
                        value={nextRentDueDate.split('/').reverse().join('-')}
                        onChange={(e) => {
                          const [yyyy, mm, dd] = e.target.value.split('-');
                          setNextRentDueDate(`${dd}/${mm}/${yyyy}`);
                        }}
                        className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div className="space-y-2">
                      {nextRentDetails.map((detail, index) => (
                        <RentDetailLine
                          key={index}
                          detail={detail}
                          index={index}
                          isReadOnly={index < 2}
                          isRemovable={index >= 2}
                          onUpdateTitle={(value) => updateNextRentDetail(index, 'title', value)}
                          onUpdateValue={(value) => updateNextRentDetail(index, 'value', value)}
                          onRemove={() => removeNextRentDetail(index)}
                        />
                      ))}
                      {/* Regularization line - read only */}
                      <RentDetailLine
                        detail={{ 
                          title: `Régularisation des charges ${periodEnd.split('/')[2] || ''}`, 
                          value: regularizationAmount 
                        }}
                        index={nextRentDetails.length}
                        isReadOnly={true}
                        isValueReadOnly={true}
                        isRemovable={false}
                        onUpdateTitle={() => {}}
                        onUpdateValue={() => {}}
                        onRemove={() => {}}
                      />
                    </div>
                    <div className="mt-3 pt-3 border-t border-teal-200 bg-teal-50 rounded-md p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">{t('pdfModal.nextRentTotal')}</span>
                        <span className="text-lg font-bold text-gray-900">{nextRentTotal.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : type === 'rent-increase' ? (
          /* Rent Increase Form */
          <div className="space-y-4">
            {/* Dates row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.revisionLeaseDate')}</label>
                <input
                  type="text"
                  readOnly
                  value={leaseStartDate}
                  className="w-full rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.revisionRevisionDate')}</label>
                <input
                  type="date"
                  value={revisionDate.split('/').reverse().join('-')}
                  onChange={(e) => {
                    const [yyyy, mm, dd] = e.target.value.split('-');
                    setRevisionDate(`${dd}/${mm}/${yyyy}`);
                  }}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* IRL indices */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">Indice de référence des loyers (IRL)</label>
                <a
                  href="https://www.insee.fr/fr/statistiques/serie/001515333#Tableau"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-orange-600 hover:text-orange-800 underline"
                >
                  Consulter le tableau INSEE →
                </a>
              </div>
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">{t('pdfModal.revisionIRLQuarter')}</label>
                <input
                  type="text"
                  readOnly
                  value={getIRLQuarter(leaseStartDate)}
                  className="w-full rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('pdfModal.revisionOldIRL')}</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={oldIRL || ''}
                      onChange={(e) => {
                        setOldIRL(parseFloat(e.target.value) || 0);
                        if (revisionValidationErrors.oldIRL) {
                          setRevisionValidationErrors({ ...revisionValidationErrors, oldIRL: undefined as unknown as string });
                        }
                      }}
                      className={`w-full rounded-md border p-2 text-sm focus:outline-none focus:ring-2 ${revisionValidationErrors.oldIRL ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'}`}
                    />
                  </div>
                  {revisionValidationErrors.oldIRL && (
                    <p className="text-xs text-red-600 mt-1">{revisionValidationErrors.oldIRL}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('pdfModal.revisionNewIRL')}</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={newIRL || ''}
                      onChange={(e) => {
                        setNewIRL(parseFloat(e.target.value) || 0);
                        if (revisionValidationErrors.newIRL) {
                          setRevisionValidationErrors({ ...revisionValidationErrors, newIRL: undefined as unknown as string });
                        }
                      }}
                      className={`w-full rounded-md border p-2 text-sm focus:outline-none focus:ring-2 ${revisionValidationErrors.newIRL ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'}`}
                    />
                  </div>
                  {revisionValidationErrors.newIRL && (
                    <p className="text-xs text-red-600 mt-1">{revisionValidationErrors.newIRL}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Rent HC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.revisionRentHC')}</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={rentHC || ''}
                  onChange={(e) => setRentHC(parseFloat(e.target.value) || 0)}
                  className="w-full rounded-md border border-gray-300 p-2 pr-8 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>

            {/* Calculated max + new rent */}
            <div className="pt-4 border-t">
              {/* Max indexed rent display */}
              <div className="mb-3 bg-orange-50 border border-orange-200 rounded-md p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{t('pdfModal.revisionMaxIndexed')}</span>
                  <span className="text-lg font-bold text-orange-700">
                    {maxIndexedRent > 0 ? `${maxIndexedRent.toFixed(2)}€` : '—'}
                  </span>
                </div>
                {maxIndexedRent > 0 && oldIRL > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    ({rentHC.toFixed(2)} × {newIRL.toFixed(2)}) ÷ {oldIRL.toFixed(2)}
                  </p>
                )}
              </div>

              {/* New rent input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.revisionNewRent')}</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={newRent || ''}
                    onChange={(e) => {
                      setNewRentManuallyEdited(true);
                      setNewRent(parseFloat(e.target.value) || 0);
                      if (revisionValidationErrors.newRent) {
                        setRevisionValidationErrors({ ...revisionValidationErrors, newRent: undefined as unknown as string });
                      }
                    }}
                    className={`w-full rounded-md border p-2 pr-8 text-sm focus:outline-none focus:ring-2 ${revisionValidationErrors.newRent ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                </div>
                {revisionValidationErrors.newRent && (
                  <p className="text-xs text-red-600 mt-1">{revisionValidationErrors.newRent}</p>
                )}
              </div>
            </div>
          </div>
          ) : (
          /* Receipt / Payment Notice Form */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{dateLabel}</label>
              <input
                type="date"
                value={moneyDate.split('/').reverse().join('-')}
                onChange={(e) => {
                  const [yyyy, mm, dd] = e.target.value.split('-');
                  setMoneyDate(`${dd}/${mm}/${yyyy}`);
                }}
                className={type === 'receipt' 
                  ? "w-full rounded-md border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  : "w-full rounded-md border border-gray-300 p-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.forMonth')}</label>
              <input
                type="month"
                value={`${forMonth.getFullYear()}-${String(forMonth.getMonth() + 1).padStart(2, '0')}`}
                onChange={(e) => {
                  const [yyyy, mm] = e.target.value.split('-').map(Number);
                  setForMonth(new Date(yyyy, mm - 1, 1));
                }}
                className={type === 'receipt' 
                  ? "w-full rounded-md border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  : "w-full rounded-md border border-gray-300 p-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                }
              />
            </div>
            
            {/* Partial Month Section */}
            <div className="pt-2 border-t">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPartialMonth}
                  onChange={(e) => setIsPartialMonth(e.target.checked)}
                  className={type === 'receipt'
                    ? "w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    : "w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  }
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium">{t('pdfModal.partialMonth')}</span>
                  <span className="text-gray-500 block text-xs">{t('pdfModal.partialMonthDescription')}</span>
                </span>
              </label>
              
              {isPartialMonth && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.startDate')}</label>
                    <input
                      type="number"
                      min="1"
                      max={new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate()}
                      value={startDay}
                      onChange={(e) => {
                        if (e.target.value === '') {
                          setStartDay(1);
                          return;
                        }
                        const day = parseInt(e.target.value);
                        if (!isNaN(day)) {
                          const maxDay = new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate();
                          setStartDay(Math.min(Math.max(1, day), maxDay));
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || parseInt(e.target.value) < 1) {
                          setStartDay(1);
                        }
                      }}
                      className={type === 'receipt'
                        ? "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        : "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfModal.endDate')}</label>
                    <input
                      type="number"
                      min="1"
                      max={new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate()}
                      value={endDay}
                      onChange={(e) => {
                        if (e.target.value === '') {
                          const maxDay = new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate();
                          setEndDay(maxDay);
                          return;
                        }
                        const day = parseInt(e.target.value);
                        if (!isNaN(day)) {
                          const maxDay = new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate();
                          setEndDay(Math.min(Math.max(1, day), maxDay));
                        }
                      }}
                      onBlur={(e) => {
                        const maxDay = new Date(forMonth.getFullYear(), forMonth.getMonth() + 1, 0).getDate();
                        if (e.target.value === '' || parseInt(e.target.value) < 1) {
                          setEndDay(maxDay);
                        }
                      }}
                      className={type === 'receipt'
                        ? "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        : "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Rent Details Section */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">{t('pdfModal.rentDetails')}</label>
                <button
                  type="button"
                  onClick={addRentDetail}
                  className={type === 'receipt'
                    ? "text-sm px-3 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors flex items-center"
                    : "text-sm px-3 py-1 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors flex items-center"
                  }
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('pdfModal.addLine')}
                </button>
              </div>
              
              <div className="space-y-2">
                {rentDetails.map((detail, index) => (
                  <RentDetailLine
                    key={index}
                    detail={detail}
                    index={index}
                    isReadOnly={index < 2}
                    isRemovable={index >= 2}
                    validationError={validationErrors[index]}
                    onUpdateTitle={(value) => updateRentDetail(index, 'title', value)}
                    onUpdateValue={(value) => updateRentDetail(index, 'value', value)}
                    onRemove={() => removeRentDetail(index)}
                  />
                ))}
              </div>
              
              <div className={type === 'receipt'
                ? "mt-3 pt-3 border-t border-indigo-200 bg-indigo-50 rounded-md p-3"
                : "mt-3 pt-3 border-t border-purple-200 bg-purple-50 rounded-md p-3"
              }>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{t('pdfModal.total')}</span>
                  <span className="text-lg font-bold text-gray-900">{totalRent.toFixed(2)}€</span>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={generating}
          >
            {t('pdfModal.cancel')}
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className={type === 'receipt'
              ? "inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              : type === 'payment-notice'
                ? "inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                : type === 'charge-regularization'
                  ? "inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  : "inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            }
          >
            {generating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('pdfModal.generating')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('pdfModal.generate')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

